'use client'

import { useState, useEffect } from 'react'
import { Menu, X } from 'lucide-react'
import { useLanguage } from '@/app/LanguageContext'
import { LanguageToggle } from '@/components/LanguageToggle'
import { useT } from '@/lib/useT'
import { ERP_LOGIN_URL } from '@/lib/erp'

export default function Navbar() {
  const { language } = useLanguage()
  const t = useT()
  const [open, setOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [activeHash, setActiveHash] = useState('')

  const navLinks = [
    { label: t('nav.platform'), href: '#platform', key: 'nav.platform' },
    { label: t('nav.pricing'), href: '#pricing', key: 'nav.pricing' },
    { label: t('nav.testimonials'), href: '#testimonials', key: 'nav.testimonials' },
    { label: t('nav.faq'), href: '#faq', key: 'nav.faq' },
  ]

  useEffect(() => {
    let ticking = false
    let lastScrollY = 0

    const updateScroll = () => {
      setScrolled(lastScrollY > 50)
      ticking = false
    }

    const onScroll = () => {
      lastScrollY = window.scrollY
      if (!ticking) {
        requestAnimationFrame(updateScroll)
        ticking = true
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    const ids = navLinks.map((l) => l.href.replace('#', ''))
    const observers: IntersectionObserver[] = []
    ids.forEach((id) => {
      const el = document.getElementById(id)
      if (!el) return
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActiveHash(`#${id}`) },
        { rootMargin: '-30% 0px -60% 0px' }
      )
      obs.observe(el)
      observers.push(obs)
    })
    return () => observers.forEach((o) => o.disconnect())
  }, [navLinks])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-colors duration-[200ms] ease-out ${
          scrolled
            ? 'bg-[#FFF9F2] shadow-sm border-b border-[#E2E4E5]'
            : 'bg-transparent shadow-none'
        }`}
        style={{ height: '64px' }}
      >
        <div className="mx-auto flex h-full max-w-[1200px] items-center justify-between px-6">
          {/* Logo — waveform symbol + "Alnajah ERP" text */}
          <a
            href="/"
            className="flex items-center gap-2.5 shrink-0 rounded-[8px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FFC992]"
            aria-label="Alnajah ERP — Home"
          >
            <img
              src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/logo-symbol-VRM6CgqMUreEttB8e2HxyWH6JlfMgz.png"
              alt=""
              aria-hidden="true"
              className="h-9 w-auto"
            />
            <span
              className={`font-bold text-[15px] tracking-tight leading-none transition-colors duration-[220ms] ${
                scrolled ? 'text-[#0A171D]' : 'text-white'
              }`}
            >
              Alnajah <span className={scrolled ? 'text-[#003F47]' : 'text-[#FFBD76]'}>ERP</span>
            </span>
          </a>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-0.5" aria-label="Primary navigation">
            {navLinks.map((link) => {
              const active = activeHash === link.href
              return (
                <a
                  key={link.href}
                  href={link.href}
                  className={`relative px-4 py-2 rounded-[8px] text-sm font-medium transition-all duration-[140ms] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FFC992] ${
                    active
                      ? scrolled
                        ? 'text-[#003F47] bg-[#E4ECED]'
                        : 'text-white bg-white/15'
                      : scrolled
                        ? 'text-[#16242B] hover:bg-[#E4ECED] hover:text-[#003F47]'
                        : 'text-[#FFF6E9] hover:bg-white/10 hover:text-white'
                  }`}
                >
                  {link.label}
                  {active && (
                    <span
                      className={`absolute bottom-1 left-1/2 -translate-x-1/2 h-0.5 w-4 rounded-full ${
                        scrolled ? 'bg-[#003F47]' : 'bg-[#FFBD76]'
                      }`}
                      aria-hidden="true"
                    />
                  )}
                </a>
              )
            })}
          </nav>

          {/* CTA group */}
          <div className="hidden md:flex items-center gap-3">
            {/* Language toggle */}
            <LanguageToggle />

            <a
              href={ERP_LOGIN_URL}
              className={`px-4 py-2 text-sm font-medium rounded-[8px] transition-all duration-[140ms] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FFC992] ${
                scrolled
                  ? 'text-[#003F47] hover:text-[#0A171D] hover:bg-[#E4ECED]'
                  : 'text-[#FFF6E9] hover:text-white hover:bg-white/10'
              }`}
            >
              {t('nav.login')}
            </a>
            <a
              href="#signup"
              className="btn-press px-5 py-2 text-sm font-semibold text-[#0A171D] bg-[#FFBD76] rounded-[999px] hover:bg-[#F0A24E] transition-all duration-[140ms] shadow-[0_8px_24px_rgba(255,189,118,0.35)] hover:shadow-[0_8px_28px_rgba(255,189,118,0.45)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FFC992]"
            >
              {t('nav.getStarted')}
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            className={`md:hidden flex items-center justify-center w-9 h-9 rounded-[8px] transition-colors duration-[140ms] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FFC992] ${
              scrolled ? 'text-[#0A171D] hover:bg-[#FFF6E9]' : 'text-white hover:bg-white/10'
            }`}
            onClick={() => setOpen(!open)}
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            aria-controls="mobile-nav"
          >
            {open ? <X size={20} aria-hidden="true" /> : <Menu size={20} aria-hidden="true" />}
          </button>
        </div>
      </header>

      {/* Mobile drawer */}
      <nav
        id="mobile-nav"
        className={`fixed inset-x-0 top-[64px] z-40 md:hidden transition-all duration-[360ms] ease-[cubic-bezier(0.16,1,0.3,1)] ${
          open
            ? 'opacity-100 visible'
            : 'opacity-0 invisible pointer-events-none'
        }`}
      >
        <div className="bg-white border-b border-[#E2E4E5] p-4 space-y-2">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="block px-4 py-2.5 text-sm font-medium text-[#16242B] hover:bg-[#FFF6E9] hover:text-[#003F47] rounded-[8px] transition-colors"
              onClick={() => setOpen(false)}
            >
              {link.label}
            </a>
          ))}
          <div className="px-4 py-2">
            <LanguageToggle />
          </div>
          <a
            href={ERP_LOGIN_URL}
            className="block px-4 py-2.5 text-sm font-medium text-[#003F47] hover:bg-[#FFF6E9] rounded-[8px] transition-colors"
            onClick={() => setOpen(false)}
          >
            {t('nav.login')}
          </a>
          <a
            href="#signup"
            className="block w-full px-4 py-2.5 text-center text-sm font-semibold text-[#0A171D] bg-[#FFBD76] rounded-[8px] hover:bg-[#F0A24E] transition-colors"
            onClick={() => setOpen(false)}
          >
            {t('nav.getStarted')}
          </a>
        </div>
      </nav>
    </>
  )
}
